import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { API_CONFIG, ApiError } from '@/lib/api';
import { showApiError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  Send,
  Smile,
  Paperclip,
  Users,
  Edit2,
  Trash2,
  Check,
  X,
  Reply,
  Copy,
  ChevronDown,
  Hash,
  AtSign,
  Star,
  StarOff,
  Forward,
} from 'lucide-react';
import { format, isValid } from 'date-fns';
import { formatDateSeparator, parseUTCDate } from '@/utils/dateUtils';
import { useStarredMessages } from '@/hooks/useStarredMessages';
import { ForwardMessageDialog } from './ForwardMessageDialog';

function extractDateStr(msg: any): string | null {
  if (typeof msg.created_at === 'string' && msg.created_at.length > 0) return msg.created_at;
  if (typeof msg.updated_at === 'string' && msg.updated_at.length > 0) return msg.updated_at;
  if (typeof msg.sk === 'string' && msg.sk.includes('#')) return msg.sk.split('#')[0];
  return null;
}

function safeFormatTime(dateStr: string | undefined | null): string {
  if (!dateStr) return format(new Date(), 'h:mm a');
  const raw = typeof dateStr === 'string' ? dateStr : String(dateStr);
  const d = parseUTCDate(raw);
  return d ? format(d, 'h:mm a') : format(new Date(), 'h:mm a');
}

function wasEdited(createdAt: string | undefined | null, updatedAt: string | undefined | null): boolean {
  if (!updatedAt || !createdAt) return false;
  const created = new Date(createdAt).getTime();
  const updated = new Date(updatedAt).getTime();
  if (isNaN(created) || isNaN(updated)) return false;
  return Math.abs(updated - created) > 2000;
}

interface ChatWindowProps {
  roomId: string;
  roomName?: string;
  className?: string;
  variant?: 'default' | 'cardTheme';
  headerSubtitle?: string;
  composerPlaceholder?: string;
}

export function ChatWindow({
  roomId,
  roomName,
  className,
  variant = 'default',
  headerSubtitle,
  composerPlaceholder,
}: ChatWindowProps) {
  const isCardTheme = variant === 'cardTheme';
  const {
    isConnected,
    messages,
    sendMessage,
    setTyping,
    joinRoom,
    leaveRoom,
    editMessage,
    deleteMessage,
    currentUser,
    typingUsers: contextTypingUsers,
    teamMembers
  } = useChat();
  const { toast } = useToast();

  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const typingNames = contextTypingUsers
    .filter(t => t.roomId === roomId)
    .map(t => {
      const member = teamMembers?.find(m => m.id === t.userId);
      return member?.full_name || member?.email || 'Someone';
    });
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState<any>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [forwardingMessage, setForwardingMessage] = useState<any>(null);
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const { isStarred, toggleStar } = useStarredMessages(currentUser?.id);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const roomMessages = useMemo(() => (
    messages
      .filter(msg => msg.room_id === roomId)
      .sort((a, b) => {
        const da = parseUTCDate(extractDateStr(a));
        const db = parseUTCDate(extractDateStr(b));
        if (!da && !db) return 0;
        if (!da) return -1;
        if (!db) return 1;
        return da.getTime() - db.getTime();
      })
  ), [messages, roomId]);

  const visibleMessages = useMemo(
    () => (showStarredOnly ? roomMessages.filter(msg => isStarred(msg.id)) : roomMessages),
    [roomMessages, showStarredOnly, isStarred],
  );

  const commonEmojis = useMemo(() => ([
    '😀', '😃', '😄', '😁', '😊', '😍', '😘', '🤔',
    '😎', '😭', '😤', '😡', '👍', '👎', '🙏', '👏',
    '🔥', '💯', '🎉', '❤️', '✅', '❌', '⭐', '⚡'
  ]), []);

  useEffect(() => {
    setShowStarredOnly(false);
  }, [roomId]);

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    setHistoryLoaded(false);
    void Promise.resolve(joinRoom(roomId)).finally(() => {
      if (!cancelled) setHistoryLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [roomId, joinRoom]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleTyping = (value: string) => {
    setMessage(value);

    if (!isTyping && value.trim()) {
      setIsTyping(true);
      setTyping(true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      setTyping(false);
    }, 1000);
  };

  const insertAtCursor = (text: string) => {
    const input = inputRef.current;
    if (!input) {
      setMessage((prev) => prev + text);
      return;
    }

    const start = input.selectionStart ?? message.length;
    const end = input.selectionEnd ?? message.length;
    const before = message.slice(0, start);
    const after = message.slice(end);
    const next = before + text + after;
    setMessage(next);

    setTimeout(() => {
      input.focus();
      const pos = start + text.length;
      input.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertEmoji = (emoji: string) => {
    insertAtCursor(emoji);
    setEmojiPickerOpen(false);
  };

  const uploadAttachment = async (file: File) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Not authenticated');

    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_CONFIG.BASE_URL}/chat/upload-attachment`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.success === false) {
      throw ApiError.fromResult(json, 'Failed to upload attachment');
    }

    return json?.data || json;
  };

  const handlePickFile = () => {
    if (!isConnected || isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isConnected) return;

    setIsUploading(true);
    try {
      const uploaded = await uploadAttachment(file);
      const isImage = file.type.startsWith('image/');

      const placeholder = isImage ? `🖼️ ${file.name}` : `📎 ${file.name}`;
      setShowStarredOnly(false);
      sendMessage(placeholder, isImage ? 'image' : 'file', {
        url: uploaded.url,
        key: uploaded.key,
        originalName: uploaded.originalName || file.name,
        mimeType: uploaded.mimeType || file.type,
        size: uploaded.size || file.size,
      });
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Upload failed', description: 'Failed to upload attachment' } });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = () => {
    if (!message.trim()) return;
    if (editingMessageId) {
      handleSaveEdit();
      return;
    }
    const content = message.trim();
    const replyMeta = replyingToMessage
      ? {
          replyTo: {
            id: replyingToMessage.id,
            senderId: replyingToMessage.sender_id,
            senderName: senderDisplayName(replyingToMessage),
            content: replyingToMessage.content?.substring(0, 200) || '',
          },
        }
      : undefined;
    setMessage('');
    setReplyingToMessage(null);
    setIsTyping(false);
    setTyping(false);
    setShowStarredOnly(false);
    void Promise.resolve(
      replyMeta ? sendMessage(content, 'text', replyMeta) : sendMessage(content),
    ).then((ok) => {
      if (ok === false) {
        setMessage(content);
        toast({
          title: 'Message not sent',
          description: 'Could not send this message. Try again.',
          variant: 'destructive',
        });
      }
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && editingMessageId) {
      e.preventDefault();
      handleCancelEdit();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const senderDisplayName = (msg: { sender?: { full_name?: string; email?: string } | null }) =>
    msg?.sender?.full_name || msg?.sender?.email || 'Unknown';

  const handleEditMessage = (messageId: string, currentContent: string) => {
    setEditingMessageId(messageId);
    setEditContent(currentContent);
    setMessage(currentContent);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSaveEdit = () => {
    if (editingMessageId && message.trim()) {
      editMessage(editingMessageId, message.trim());
      setEditingMessageId(null);
      setEditContent('');
      setMessage('');
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
    setMessage('');
  };

  const handleDeleteMessage = (messageId: string) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMessage(messageId);
    }
  };

  const isCurrentUserMessage = (senderId: string) => {
    return currentUser?.id === senderId;
  };

  const handleReplyToMessage = (message: any) => {
    setReplyingToMessage(message);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCancelReply = () => {
    setReplyingToMessage(null);
  };

  const jumpToMessage = (messageId: string) => {
    const messageElement = messageRefs.current[messageId];
    if (!messageElement && showStarredOnly) {
      setShowStarredOnly(false);
      setTimeout(() => jumpToMessage(messageId), 0);
      return;
    }
    if (messageElement) {
      setHighlightedMessageId(messageId);

      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 3000);
    }
  };

  const parseMetadata = (raw: any) => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return null;
  };

  const renderMessageTextOrAttachment = (msg: any, isCurrentUser: boolean) => {
    const meta = parseMetadata(msg.metadata) || {};
    const time = safeFormatTime(extractDateStr(msg));
    const edited = wasEdited(msg.created_at, msg.updated_at);
    const forwardedFrom = meta?.forwarded?.originalSenderName;
    const timeStamp = (
      <span
        className={`inline-flex items-center gap-1 ml-2 text-xs whitespace-nowrap flex-shrink-0 ${
          isCardTheme && isCurrentUser ? 'text-white/80' : 'text-gray-600 opacity-80'
        }`}
        style={{ verticalAlign: 'bottom' }}
      >
        {edited && <span>(edited)</span>}
        {time}
      </span>
    );
    const forwardedLabel = forwardedFrom ? (
      <div className="flex items-center gap-1 text-[11px] italic text-gray-500 mb-1">
        <Forward className="h-3 w-3" />
        <span>Forwarded{forwardedFrom ? ` from ${forwardedFrom}` : ''}</span>
      </div>
    ) : null;

    const resolveDisplayName = (nameOrEmail: string, senderId?: string) => {
      if (senderId) {
        const member = teamMembers?.find(m => m.id === senderId);
        if (member?.full_name) return member.full_name;
      }
      if (nameOrEmail && nameOrEmail.includes('@')) {
        const member = teamMembers?.find(m => m.email === nameOrEmail);
        if (member?.full_name) return member.full_name;
      }
      return nameOrEmail || 'Unknown';
    };

    let body: React.ReactNode;

    if (msg.message_type === 'image' && meta.url) {
      body = (
        <div>
          <img
            src={meta.url}
            alt={meta.originalName || 'image'}
            className="max-w-[260px] max-h-[260px] rounded-md object-cover"
            loading="lazy"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs opacity-80 truncate">{meta.originalName}</span>
            {timeStamp}
          </div>
        </div>
      );
    } else if ((msg.message_type === 'file' || msg.message_type === 'video' || msg.message_type === 'audio') && meta.url) {
      body = (
        <div>
          {msg.message_type === 'video' ? (
            <video src={meta.url} controls className="max-w-[260px] max-h-[260px] rounded-md" />
          ) : msg.message_type === 'audio' ? (
            <audio src={meta.url} controls className="max-w-[260px]" />
          ) : (
            <a
              href={meta.url}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2 text-sm break-all"
            >
              {meta.originalName || msg.content}
            </a>
          )}
          <div className="flex items-center justify-end mt-1">
            {timeStamp}
          </div>
        </div>
      );
    } else if (meta?.replyTo) {
      const replyInfo = meta.replyTo;
      const repliedToName = resolveDisplayName(replyInfo.senderName, replyInfo.senderId);
      body = (
        <div className="space-y-2">
          <div
            className="rounded-lg bg-gray-50 border-l-[3px] border-emerald-500 overflow-hidden cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => replyInfo.id && jumpToMessage(replyInfo.id)}
          >
            <div className="px-3 py-2 min-w-0">
              <div className="text-[11px] font-semibold text-emerald-600 truncate">{repliedToName}</div>
              <div className="text-xs text-gray-500 truncate mt-0.5">{replyInfo.content || '...'}</div>
            </div>
          </div>
          <div className="inline">
            <span>{msg.content}</span>
            {timeStamp}
          </div>
        </div>
      );
    } else if (msg.content.startsWith('Replying to')) {
      const firstColon = msg.content.indexOf(':');
      const rawName = msg.content.substring(0, firstColon).replace('Replying to ', '');
      const replyText = msg.content.substring(firstColon + 1).trim();
      const repliedToName = resolveDisplayName(rawName);
      body = (
        <div className="space-y-2">
          <div className="rounded-lg bg-gray-50 border-l-[3px] border-emerald-500 overflow-hidden">
            <div className="px-3 py-2 min-w-0">
              <div className="text-[11px] font-semibold text-emerald-600 truncate">{repliedToName}</div>
              <div className="text-xs text-gray-500 truncate mt-0.5">{replyText}</div>
            </div>
          </div>
          <div className="inline">
            <span>{replyText}</span>
            {timeStamp}
          </div>
        </div>
      );
    } else {
      body = (
        <div className="flex items-end justify-between gap-2 flex-nowrap">
          <span className="whitespace-pre-wrap break-words flex-1 min-w-0">{msg.content}</span>
          {timeStamp}
        </div>
      );
    }

    return <>{forwardedLabel}{body}</>;
  };

  const handleCopyMessage = (msg: any) => {
    const meta = parseMetadata(msg.metadata) || {};
    const textToCopy = meta.url || msg.content;
    navigator.clipboard.writeText(textToCopy).then(() => {
      toast({ variant: "success", title: 'Copied to clipboard' });
    });
    setContextMenu(null);
  };

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; msg: any } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contextMenu || !contextMenuRef.current) return;
    const el = contextMenuRef.current;
    const rect = el.getBoundingClientRect();
    let { x, y } = contextMenu;
    let changed = false;

    if (rect.bottom > window.innerHeight - 10) {
      y = contextMenu.y - rect.height - 5;
      changed = true;
    }
    if (rect.right > window.innerWidth - 10) {
      x = window.innerWidth - rect.width - 10;
      changed = true;
    }
    if (x < 10) {
      x = 10;
      changed = true;
    }
    if (changed) {
      setContextMenu(prev => prev ? { ...prev, x, y } : null);
    }
  }, [contextMenu]);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [contextMenu]);

  return (
    <div className={`flex flex-col h-full min-h-0 bg-background ${className}`} style={{ height: '100%' }}>
      <div className={cn(
        'flex items-center justify-between sticky top-0 z-10 border-b',
        isCardTheme
          ? 'border-[#EAECF0] bg-white px-4 py-3'
          : 'px-6 py-4 bg-white/80 backdrop-blur-md shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]',
      )}>
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            isCardTheme ? 'bg-brand-soft text-brand-cta' : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-sm text-white',
          )}>
            <Hash className="h-5 w-5" />
          </div>
          <div className="flex flex-col">
            <h3 className="font-bold text-gray-900 text-[1.05rem] tracking-tight">{roomName || 'team-chat'}</h3>
            {isCardTheme ? (
              <span className="mt-0.5 text-[12px] text-[#667085]">
                {headerSubtitle || 'Use @ to mention'}
              </span>
            ) : (
              <span className="text-xs font-medium text-emerald-600 flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Active Now
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showStarredOnly ? 'secondary' : 'ghost'}
            aria-pressed={showStarredOnly}
            size="icon"
            title={showStarredOnly
              ? 'Filter on: starred messages only — click to show all messages'
              : 'Filter this chat to starred messages only'}
            onClick={() => setShowStarredOnly((v) => !v)}
            data-testid="team-chat-starred-toggle"
          >
            <Star className={`h-4 w-4 ${showStarredOnly ? 'fill-yellow-400 text-yellow-500' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-2 sm:p-4 overflow-y-auto bg-[#F5F7F9]">
        <div className="space-y-3">
          {showStarredOnly && visibleMessages.length > 0 && (
            <div
              className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2 text-center"
              data-testid="team-chat-starred-banner"
            >
              <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-500 shrink-0" />
              <span className="text-xs text-[#854d0e]">
                Filtered to your {visibleMessages.length === 1
                  ? '1 starred message'
                  : `${visibleMessages.length} starred messages`}. The
                {' '}{roomMessages.length - visibleMessages.length === 1
                  ? 'other message is'
                  : `other ${roomMessages.length - visibleMessages.length} messages are`}{' '}
                hidden, not deleted.
              </span>
              <button
                type="button"
                className="text-xs font-medium text-[#854d0e] underline underline-offset-2 hover:no-underline"
                onClick={() => setShowStarredOnly(false)}
                data-testid="team-chat-starred-banner-show-all"
              >
                Show all messages
              </button>
            </div>
          )}
          {visibleMessages.length === 0 && (
            !historyLoaded && roomMessages.length === 0 ? (
              <div
                className="flex items-center justify-center py-12 text-sm text-muted-foreground"
                data-testid="team-chat-loading"
              >
                Loading messages…
              </div>
            ) : showStarredOnly ? (
              <div
                className="flex flex-col items-center justify-center py-12 px-4 text-center"
                data-testid="team-chat-starred-empty"
              >
                <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center mb-4">
                  <Star className="h-8 w-8 fill-yellow-400 text-yellow-500" />
                </div>
                <h4 className="font-medium text-foreground">
                  You're viewing starred messages only
                </h4>
                {roomMessages.length > 0 ? (
                  <>
                    <p className="text-sm text-muted-foreground mt-2 max-w-md">
                      The star button filters this chat down to messages you have starred,
                      and you haven't starred any yet — so there is nothing to show.
                    </p>
                    <p className="text-sm text-muted-foreground mt-2 max-w-md">
                      Nothing has been deleted: {roomMessages.length === 1
                        ? 'the 1 message in this chat is'
                        : `all ${roomMessages.length} messages in this chat are`}{' '}
                      still there. Turn the filter off to see them.
                    </p>
                    <p className="text-xs text-muted-foreground mt-3 max-w-md">
                      To star a message, hover it, open the ⌄ menu and choose Star.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground mt-2 max-w-md">
                    The star button filters this chat down to messages you have starred.
                    There are no messages here yet — once there are, open a message's ⌄ menu
                    and choose Star to pin it to this view.
                  </p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => setShowStarredOnly(false)}
                  data-testid="team-chat-starred-empty-show-all"
                >
                  Show all messages
                </Button>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center py-12 px-4 text-center"
                data-testid="team-chat-empty"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                  <Hash className="h-8 w-8 text-muted-foreground" />
                </div>
                <h4 className="font-medium text-foreground">No messages yet</h4>
                <p className="text-sm text-muted-foreground mt-2">
                  Say hello to {roomName || 'the team'} to start the conversation.
                </p>
              </div>
            )
          )}
          {(() => {
            let lastDateStr = '';
            return visibleMessages.map((msg) => {
              const isCurrentUser = isCurrentUserMessage(msg.sender_id);
              const isEditing = editingMessageId === msg.id;
              const isHighlighted = highlightedMessageId === msg.id;

              let dateSeparator = null;
              const rawDate = extractDateStr(msg);
              if (rawDate) {
                const msgDate = parseUTCDate(rawDate);
                const dateStr = msgDate ? format(msgDate, 'yyyy-MM-dd') : '';
                if (dateStr && dateStr !== lastDateStr) {
                  lastDateStr = dateStr;
                  const label = formatDateSeparator(rawDate);
                  dateSeparator = (
                    <div className="flex justify-center my-3" key={`date-${dateStr}`}>
                      <span className="bg-[#e1f2fb] text-[#54656f] text-xs font-medium px-3 py-1 rounded-lg shadow-sm">
                        {label}
                      </span>
                    </div>
                  );
                }
              }

              return (
                <React.Fragment key={msg.id}>
                  {dateSeparator}
                  <div
                    ref={(el) => (messageRefs.current[msg.id] = el)}
                    className={`flex gap-2 max-w-[80%] ${isCurrentUser ? 'ml-auto flex-row-reverse' : 'mr-auto'}`}
                  >
                    {!isCurrentUser && (
                      <Avatar className="h-6 w-6 mt-1">
                        <AvatarImage src={msg.sender?.avatar_url} />
                        <AvatarFallback
                          className={
                            isCardTheme
                              ? 'bg-[#F2F4F7] text-[10px] font-semibold text-[#475467]'
                              : 'text-xs'
                          }
                        >
                          {getInitials(senderDisplayName(msg))}
                        </AvatarFallback>
                      </Avatar>
                    )}

                    <div className={`flex flex-col ${!isCurrentUser ? '' : ''}`}>
                      {!isCurrentUser && (
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-medium text-xs text-gray-500">
                            {senderDisplayName(msg)}
                          </span>
                        </div>
                      )}

                      <div className="relative group">
                        <div
                          className={`px-4 py-3 shadow-sm rounded-2xl break-words w-fit max-w-sm transition-colors duration-500 ${isCurrentUser
                            ? isCardTheme
                              ? 'bg-brand text-white rounded-br-md'
                              : 'bg-white border border-gray-200 text-gray-900 rounded-br-md'
                            : 'bg-white text-gray-900 border border-[#EAECF0] rounded-bl-md'
                            } ${isHighlighted ? 'ring-2 ring-emerald-400 bg-emerald-50' : ''}`}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setContextMenu({ x: e.clientX, y: e.clientY, msg });
                          }}
                        >
                          <div className={cn('text-sm leading-relaxed', isCardTheme && isCurrentUser ? 'text-white' : 'text-gray-900')}>
                            {renderMessageTextOrAttachment(msg, isCurrentUser)}
                          </div>
                        </div>

                        {!isEditing && (
                          <button
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity rounded-full p-0.5 bg-black/5 hover:bg-black/10 text-gray-500 z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (contextMenu?.msg?.id === msg.id) {
                                setContextMenu(null);
                                return;
                              }
                              const rect = e.currentTarget.getBoundingClientRect();
                              const x = isCurrentUser ? rect.right - 180 : rect.left;
                              setContextMenu({ x, y: rect.bottom + 4, msg });
                            }}
                            data-testid={`team-chat-msg-menu-${msg.id}`}
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {isStarred(msg.id) && (
                          <span
                            className={`absolute -bottom-1 ${isCurrentUser ? '-left-1' : '-right-1'} bg-white rounded-full p-0.5 shadow-sm z-10`}
                            title="Starred"
                            data-testid={`team-chat-msg-star-${msg.id}`}
                          >
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-500" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            });
          })()}

          {contextMenu && (
            <div
              ref={contextMenuRef}
              className="fixed z-50 min-w-[180px] bg-popover border border-border rounded-lg shadow-xl py-1 animate-in fade-in-0 zoom-in-95"
              style={{ top: contextMenu.y, left: contextMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                onClick={() => { handleReplyToMessage(contextMenu.msg); setContextMenu(null); }}
              >
                <Reply className="h-4 w-4 text-muted-foreground" />
                Reply
              </button>
              <button
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                onClick={() => handleCopyMessage(contextMenu.msg)}
              >
                <Copy className="h-4 w-4 text-muted-foreground" />
                Copy
              </button>
              <button
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                onClick={() => { toggleStar(contextMenu.msg.id); setContextMenu(null); }}
                data-testid="team-chat-menu-star"
              >
                {isStarred(contextMenu.msg.id) ? (
                  <>
                    <StarOff className="h-4 w-4 text-muted-foreground" />
                    Unstar
                  </>
                ) : (
                  <>
                    <Star className="h-4 w-4 text-muted-foreground" />
                    Star
                  </>
                )}
              </button>
              <button
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                onClick={() => { setForwardingMessage(contextMenu.msg); setContextMenu(null); }}
                data-testid="team-chat-menu-forward"
              >
                <Forward className="h-4 w-4 text-muted-foreground" />
                Forward
              </button>
              {isCurrentUserMessage(contextMenu.msg.sender_id) && (
                <>
                  <div className="border-t border-border my-1" />
                  <button
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
                    onClick={() => { handleEditMessage(contextMenu.msg.id, contextMenu.msg.content); setContextMenu(null); }}
                  >
                    <Edit2 className="h-4 w-4 text-muted-foreground" />
                    Edit
                  </button>
                  <button
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left text-destructive"
                    onClick={() => { handleDeleteMessage(contextMenu.msg.id); setContextMenu(null); }}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </>
              )}
            </div>
          )}

          {typingNames.length > 0 && (
            <div className="flex gap-3">
              <div className="h-8 w-8" />
              <div className="text-sm text-muted-foreground italic">
                {typingNames.join(', ')} {typingNames.length === 1 ? 'is' : 'are'} typing...
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {replyingToMessage && (
        <div className="px-4 py-3 bg-muted border-t">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Reply className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-foreground">
                    Replying to {senderDisplayName(replyingToMessage)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {safeFormatTime(extractDateStr(replyingToMessage))}
                  </span>
                </div>
                <div
                  className="text-sm text-muted-foreground bg-background rounded-md p-2 border cursor-pointer hover:bg-accent transition-colors"
                  onClick={() => jumpToMessage(replyingToMessage.id)}
                  title="Click to jump to message"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-4 bg-primary rounded-full flex-shrink-0"></div>
                    <span className="truncate">
                      {replyingToMessage.content.length > 100
                        ? replyingToMessage.content.substring(0, 100) + '...'
                        : replyingToMessage.content}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelReply}
              className="h-6 w-6 p-0 flex-shrink-0"
              title="Cancel reply"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {editingMessageId && (
        <div className="px-4 py-2 bg-muted border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Edit2 className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-foreground">Editing message</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelEdit}
              className="h-6 w-6 p-0 flex-shrink-0"
              title="Cancel edit"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className={cn(isCardTheme ? 'border-t border-[#EAECF0] bg-white px-4 py-3' : 'p-4 border-t')}>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelected}
        />
        {isCardTheme ? (
          <>
            <div className="flex items-center gap-1 rounded-xl border border-[#E4E7EC] bg-[#F9FAFB] px-2 py-1.5">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#475467] hover:bg-[#F2F4F7] hover:text-[#101828] disabled:opacity-100" onClick={handlePickFile} disabled={isUploading} title="Attach file">
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#475467] hover:bg-[#F2F4F7] hover:text-[#101828]" onClick={() => insertAtCursor('@')} title="Mention">
                <AtSign className="h-4 w-4" />
              </Button>
              <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#475467] hover:bg-[#F2F4F7] hover:text-[#101828]" title="Emoji">
                    <Smile className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="start">
                  <div className="grid grid-cols-8 gap-1">
                    {commonEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="h-8 w-8 rounded hover:bg-muted flex items-center justify-center text-lg"
                        onClick={() => insertEmoji(emoji)}
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Input
                ref={inputRef}
                value={message}
                onChange={(e) => handleTyping(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={replyingToMessage ? 'Type your reply...' : (composerPlaceholder || 'Message the team... use @ to mention')}
                className="h-9 flex-1 border-0 bg-transparent shadow-none text-[#101828] placeholder:text-[#98A2B3] focus-visible:ring-0 disabled:cursor-text disabled:opacity-100"
              />
              <Button
                variant="ghost"
                onClick={handleSendMessage}
                disabled={!message.trim() || isUploading}
                size="icon"
                className="h-9 w-9 shrink-0 rounded-lg bg-brand text-white hover:bg-brand-hover hover:text-white disabled:pointer-events-none disabled:bg-brand disabled:text-white disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" onClick={handlePickFile} disabled={!isConnected || isUploading}>
            <Paperclip className="h-4 w-4" />
          </Button>
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={message}
              onChange={(e) => handleTyping(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={replyingToMessage ? "Type your reply..." : "Type a message..."}
              disabled={!isConnected}
              className="pr-10"
            />
            <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8"
                  disabled={!isConnected}
                >
                  <Smile className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end">
                <div className="grid grid-cols-8 gap-1">
                  {commonEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="h-8 w-8 rounded hover:bg-muted flex items-center justify-center text-lg"
                      onClick={() => insertEmoji(emoji)}
                      title={emoji}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!message.trim() || !isConnected || isUploading}
            size="icon"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        )}
      </div>

      <ForwardMessageDialog
        open={!!forwardingMessage}
        onOpenChange={(o) => !o && setForwardingMessage(null)}
        message={forwardingMessage}
      />
    </div>
  );
}
